/**
 * "Needs ticket" / "Awaiting customer" / "Ready to invoice" on the Assemblies
 * register — where each assembly's finished runs stand on the way to a bill.
 *
 * FROM ONE `GET /downhole/runs`, grouped by assembly here: a run that can go
 * on a ticket (closed or not-run, on no ticket, not invoiced — the API's own
 * lockRunsForTicket rule), an office-approved ticket, and a customer-approved
 * run not on a live invoice.
 *
 * NOT FROM API-A's `ticket_summary` (api#928), and that is deliberate. Its
 * `closed_runs_without_ticket` is "run out and on no live ticket", which is a
 * different question from "needs a ticket": it COUNTS a closed run billed
 * before tickets existed (the API refuses to ticket it, RUN_ALREADY_INVOICED)
 * and does NOT count a mobilized-not-run run, which does need one. Measured on
 * a local copy of a0a0 (2026-10-07): DH-A-1002, -1005 and -1008 read 1 for
 * runs already invoiced, and production a0a0 has six such pre-ticket
 * invoices. Binding it as "Needs ticket" would badge assemblies with nothing
 * to do. Reported to the API; switch to the summary when it counts what the
 * label says — and then use it for every pill, never one pill from each.
 *
 * A row whose runs have not arrived shows `—`, never a zero.
 */
import React from 'react';
import type { DhRun } from '../../types/downhole';
import { isTicketable } from './ticketBits';

export interface BillingIndicators {
  needsTicket: number;
  readyToSend: number | null; // null: the source cannot tell sent from unsent
  awaitingCustomer: number;
  readyToInvoice: number;
}

/** The fallback: one runs read, grouped by assembly. */
export function indicatorsFromRuns(runs: DhRun[]): Map<string, BillingIndicators> {
  const out = new Map<string, BillingIndicators>();
  const awaiting = new Map<string, Set<string>>();
  for (const r of runs) {
    const cur = out.get(r.assembly_id) ?? { needsTicket: 0, readyToSend: null, awaitingCustomer: 0, readyToInvoice: 0 };
    if (isTicketable(r)) cur.needsTicket += 1;
    if (r.ticket_status === 'office_approved' && r.dh_ticket_id) {
      const set = awaiting.get(r.assembly_id) ?? new Set<string>();
      set.add(r.dh_ticket_id);
      awaiting.set(r.assembly_id, set);
      cur.awaitingCustomer = set.size;
    }
    if (r.ticket_status === 'customer_approved' && r.invoiced === false) cur.readyToInvoice += 1;
    out.set(r.assembly_id, cur);
  }
  return out;
}

const PILL = 'inline-flex items-center gap-1 whitespace-nowrap rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.62rem] font-black uppercase leading-none text-fi-signal-ink';

/** The pills that apply. Nothing at all when the source has not arrived or nothing is pending. */
export function BillingPills({ ind }: { ind: BillingIndicators | null | undefined }) {
  if (!ind) return <span className="text-sm text-fieldiq-muted">—</span>;
  const pills: { key: string; label: string; n: number; fill: string }[] = [
    { key: 'needs', label: 'Needs ticket', n: ind.needsTicket, fill: 'bg-fi-signal-amber' },
    { key: 'send', label: 'Ready to send', n: ind.readyToSend ?? 0, fill: 'bg-fi-signal-amber' },
    { key: 'await', label: 'Awaiting customer', n: ind.awaitingCustomer, fill: 'bg-fi-signal-blue' },
    { key: 'invoice', label: 'Ready to invoice', n: ind.readyToInvoice, fill: 'bg-fi-signal-green' },
  ].filter((p) => p.n > 0);
  if (pills.length === 0) return <span className="text-sm text-fieldiq-muted">Nothing pending</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {pills.map((p) => (
        <span key={p.key} className={`${PILL} ${p.fill}`} aria-label={`${p.label}: ${p.n}`}>
          {p.label}
          <span className="tabular-nums" aria-hidden="true">{p.n}</span>
        </span>
      ))}
    </span>
  );
}
